import React from 'react';
import { FormattedMessage } from '@kyruus/intl';
import { useLocation } from 'react-router-dom';
import { Box } from '@biome-design-system/ui/Box';
import { Typography } from '@biome-design-system/ui/Typography';

import {
  type GetUpdatedSearch,
  type Log,
  type SearchSummary as SearchSummaryType
} from 'Src/types';
import { isSearchRefinerSkipped } from 'Src/utils/search-common';
import { ClearFilters } from './ClearFilters';
import { getSearchSummaryMessage } from './utils';
import { messages } from '../messages';

function getSearchDescription(searchSummary: SearchSummaryType) {
  return searchSummary.search_description ? (
    <span data-testid="search-results-description">
      &quot;{searchSummary.search_description}&quot;
    </span>
  ) : null;
}

interface SearchSummaryProps {
  totalProviders: number;
  searchSummary: SearchSummaryType;
  getUpdatedSearch: GetUpdatedSearch;
  locationName?: string | null; // location name for map-type searches
  hideSearchDescription?: boolean; // description is hidden when rendering in location page provider drawer
  log: Log;
  showClearLink?: boolean; // option to hide clear link when needed
  isLoadingOverlayShown?: boolean; // option to hide text when loading overlay is shown
}

export const SearchSummary = ({
  totalProviders,
  searchSummary,
  getUpdatedSearch,
  locationName,
  hideSearchDescription, // description is hidden when rendering in location page provider drawer
  log,
  showClearLink = true,
  isLoadingOverlayShown = false
}: SearchSummaryProps) => {
  const location = useLocation();
  const searchDescription = getSearchDescription(searchSummary);
  const showSearchDescription =
    searchDescription != null && hideSearchDescription !== true;
  const shouldShowClearLink =
    showClearLink &&
    (searchSummary.has_filters ||
      searchSummary.filter?.length ||
      searchSummary.location);

  const { messageDescriptor, testId } = getSearchSummaryMessage(
    location.search,
    locationName,
    showSearchDescription
  );
  const shouldAnnounceSearchTooGeneral = isSearchRefinerSkipped(
    location.search
  );

  return (
    <Box>
      <Typography
        className="hidden-print"
        component="h3"
        data-testid="search-summary-text"
        sx={{ display: 'inline-block', mr: 2 }}
      >
        <Typography component="span" aria-live="polite" data-testid={testId}>
          {!isLoadingOverlayShown && (
            <>
              <FormattedMessage
                {...messageDescriptor}
                values={{
                  location:
                    searchSummary.display_location || searchSummary.location,
                  providerCount: totalProviders,
                  searchTerm: searchDescription
                }}
              />
              {shouldAnnounceSearchTooGeneral && (
                <FormattedMessage
                  {...messages.searchIsTooGeneralAriaLive}
                  values={{
                    term: searchSummary.search_description,
                    letUsHelp: ''
                  }}
                >
                  {(message) => (
                    <Box
                      sx={{
                        border: 0,
                        clip: 'rect(0 0 0 0)',
                        height: 1,
                        margin: -1,
                        overflow: 'hidden',
                        padding: 0,
                        position: 'absolute',
                        whiteSpace: 'nowrap',
                        width: 1
                      }}
                    >
                      {message}
                    </Box>
                  )}
                </FormattedMessage>
              )}
            </>
          )}
        </Typography>
        {locationName && (
          <Typography component="span" aria-live="polite" sx={{ ml: 0.5 }}>
            {!isLoadingOverlayShown && (
              <FormattedMessage
                {...messages.locationName}
                values={{
                  location: locationName
                }}
              />
            )}
          </Typography>
        )}
      </Typography>
      {shouldShowClearLink && (
        <ClearFilters getUpdatedSearch={getUpdatedSearch} log={log} />
      )}
    </Box>
  );
};
